import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Clock, XCircle, BadgeCheck } from "lucide-react";
import { useAuthedApi, errMsg } from "../../lib/api";
import { PrivateDocUpload, PrivateDocButton } from "../PrivateDoc";

export default function VerificationCard({ dealer, onSaved }) {
  const api = useAuthedApi();
  const [f, setF] = useState({ gst_number: "", trade_licence: "", document_url: "", note: "", ...(dealer.verification || {}) });
  const [busy, setBusy] = useState(false);
  const status = dealer.verified ? "verified" : dealer.verification_status || "none";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.document_url) return toast.error("Upload a document photo (GST certificate or trade licence)");
    setBusy(true);
    try {
      const { gst_number, trade_licence, document_url, note } = f;
      onSaved(await api("post", "/dealers/me/verification", { gst_number, trade_licence, document_url, note }));
      toast.success("Verification request sent to admin");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  if (status === "verified") return (
    <div className="lux-card p-6 flex items-center gap-4 max-w-3xl" data-testid="verification-status-verified">
      <BadgeCheck className="w-10 h-10 text-[#D4AF37]" />
      <div><p className="font-semibold gold-text text-lg">Verified Dealer</p><p className="text-sm text-gray-400">Your store shows the gold verified badge to buyers.</p></div>
    </div>
  );
  if (status === "pending") return (
    <div className="lux-card p-6 flex items-start gap-4 max-w-3xl" data-testid="verification-status-pending">
      <Clock className="w-10 h-10 text-amber-400 shrink-0" />
      <div className="space-y-1">
        <p className="font-semibold text-lg">Verification under review</p>
        <p className="text-sm text-gray-400">Our admin is checking your documents. You'll see the gold badge once approved.</p>
        {dealer.verification && (
          <div className="text-xs text-gray-300 pt-2 space-y-1" data-testid="verification-submitted">
            <p><span className="text-gray-500">GST:</span> {dealer.verification.gst_number}</p>
            {dealer.verification.trade_licence && <p><span className="text-gray-500">Trade licence:</span> {dealer.verification.trade_licence}</p>}
            {dealer.verification.document_url?.startsWith("/api/private-files/") && <PrivateDocButton url={dealer.verification.document_url} testid="verification-submitted-doc" />}
          </div>
        )}
      </div>
    </div>
  );
  return (
    <form onSubmit={submit} className="lux-card p-6 sm:p-8 space-y-5 max-w-3xl" data-testid="verification-form">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 text-[#D4AF37] shrink-0" />
        <div>
          <p className="eyebrow">Get the verified dealer badge</p>
          <p className="text-sm text-gray-400 mt-1">Submit your business details. An admin reviews them and approves your store.</p>
        </div>
      </div>
      {status === "rejected" && (
        <p className="text-sm text-red-300 flex items-center gap-2" data-testid="verification-rejected"><XCircle className="w-4 h-4" />Previous request was rejected{dealer.verification_reason ? `: ${dealer.verification_reason}` : ""}. You can submit again.</p>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="lux-label">GST number</label><input className="lux-input" required minLength={5} value={f.gst_number} onChange={(e) => setF({ ...f, gst_number: e.target.value.toUpperCase() })} data-testid="verify-gst" /></div>
        <div><label className="lux-label">Trade licence no. (optional)</label><input className="lux-input" value={f.trade_licence} onChange={(e) => setF({ ...f, trade_licence: e.target.value })} data-testid="verify-licence" /></div>
      </div>
      <div className="sm:w-40"><label className="lux-label">Document photo</label>
        <PrivateDocUpload value={f.document_url} onChange={(u) => setF((p) => ({ ...p, document_url: u }))} testid="verify-doc" />
      </div>
      <div><label className="lux-label">Note for admin</label><textarea className="lux-textarea" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="verify-note" /></div>
      <button className="btn-gold" disabled={busy} data-testid="verify-submit">{busy ? "Sending..." : "Request verification"}</button>
    </form>
  );
}
